// Kopie z FofrLamaPOS (hooks/usePretahovani.js) — editor stolů je v pokladně i ve Správě OS
// (Správa → Provozovna → Stoly, 2026-10-20). Měň obojí stejně.
import { useEffect, useRef } from 'react'

/** Mřížka, na kterou se chytá tažení i změna velikosti. */
export const KROK = 20

export const snap = (v) => Math.round(v / KROK) * KROK

/**
 * Tažení a změna velikosti dlaždice po plátně.
 *
 * Sdílí ho editor stolů i editor sekcí — je to tatáž práce o úroveň výš
 * (dlaždice s `pozice` na plátně o daných rozměrech) a mít ji dvakrát by
 * znamenalo, že se snap, clamp a minimální rozměr dřív nebo později rozejdou.
 *
 * Souřadnice se drží v px plátna, ne obrazovky: pohyb myši se dělí měřítkem,
 * takže rozvržení vypadá stejně na tabletu i na velkém displeji.
 *
 * Každá osa má vlastní měřítko — plátno se roztahuje na celou plochu, takže
 * jeden společný přepočet by tažení rozjel: myš by ujela jinam než dlaždice.
 *
 * @param {object} p
 * @param {number} p.meritkoX       px obrazovky na jednotku plátna vodorovně
 * @param {number} p.meritkoY       totéž svisle
 * @param {number} p.platnoSirka    šířka plátna v jednotkách
 * @param {number} p.platnoVyska    výška plátna v jednotkách
 * @param {number} p.minRozmer      nejmenší povolená strana dlaždice
 * @param {(id: any, uprava: object) => void} p.onPosun
 * @returns {{ zacit: (rezim: 'tah'|'rozmer', e: PointerEvent, dlazdice: object) => void }}
 */
export function usePretahovani({
  meritkoX,
  meritkoY,
  platnoSirka,
  platnoVyska,
  minRozmer = 60,
  // Měřítko rozměru, když se dlaždice kreslí jedním měřítkem v obou osách
  // (stoly drží tvar). Bez něj platí osová měřítka.
  meritkoRozmeru = null,
  onPosun
}) {
  const tah = useRef(null)

  useEffect(() => {
    if (!meritkoX || !meritkoY) return

    const pohyb = (e) => {
      const t = tah.current
      if (!t) return
      const dx = (e.clientX - t.startX) / meritkoX
      const dy = (e.clientY - t.startY) / meritkoY

      if (t.rezim === 'tah') {
        onPosun(t.id, {
          x: Math.max(0, Math.min(platnoSirka - t.sirka, snap(t.x + dx))),
          y: Math.max(0, Math.min(platnoVyska - t.vyska, snap(t.y + dy)))
        })
      } else {
        // Úchyt je v rohu stolu kresleného kolem středu — rozměr roste
        // oběma směry, proto dvojnásobek posunu.
        const rx = meritkoRozmeru ? (2 * (e.clientX - t.startX)) / meritkoRozmeru : dx
        const ry = meritkoRozmeru ? (2 * (e.clientY - t.startY)) / meritkoRozmeru : dy
        onPosun(t.id, {
          sirka: Math.max(minRozmer, Math.min(platnoSirka - t.x, snap(t.sirka + rx))),
          vyska: Math.max(minRozmer, Math.min(platnoVyska - t.y, snap(t.vyska + ry)))
        })
      }
    }
    const konec = () => {
      tah.current = null
    }

    window.addEventListener('pointermove', pohyb)
    window.addEventListener('pointerup', konec)
    window.addEventListener('pointercancel', konec)
    return () => {
      window.removeEventListener('pointermove', pohyb)
      window.removeEventListener('pointerup', konec)
      window.removeEventListener('pointercancel', konec)
    }
  }, [meritkoX, meritkoY, platnoSirka, platnoVyska, minRozmer, meritkoRozmeru, onPosun])

  return {
    zacit(rezim, e, dlazdice) {
      e.preventDefault()
      tah.current = {
        rezim,
        id: dlazdice.id,
        startX: e.clientX,
        startY: e.clientY,
        x: dlazdice.pozice.x,
        y: dlazdice.pozice.y,
        sirka: dlazdice.pozice.sirka,
        vyska: dlazdice.pozice.vyska
      }
    }
  }
}
